import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  X, ChevronRight, ChevronLeft, Sparkles,
  FileText, Users, MessageSquare, Settings, 
  BarChart3, CreditCard, FileKey
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tour steps configuration
const TOUR_STEPS = [
  {
    id: 'welcome',
    target: null, // No specific element
    title: '¡Bienvenido a CIA Factura℗!',
    description: 'Te mostraremos las funcionalidades principales para que puedas empezar a facturar rápidamente.',
    position: 'center',
    icon: Sparkles
  },
  {
    id: 'dashboard',
    target: '[data-testid="dashboard-stats"]',
    title: 'Dashboard Principal',
    description: 'Aquí verás un resumen de tu actividad: facturas emitidas, ingresos del mes y clientes activos.',
    position: 'bottom',
    icon: BarChart3,
    route: '/dashboard'
  },
  {
    id: 'invoices',
    target: '[data-testid="nav-invoices"]',
    title: 'Facturas',
    description: 'Crea, consulta y descarga tus CFDI. Puedes emitir facturas de ingreso, notas de crédito y complementos de pago.',
    position: 'right',
    icon: FileText,
    route: '/dashboard/invoices'
  },
  {
    id: 'clients',
    target: '[data-testid="nav-clients"]',
    title: 'Clientes',
    description: 'Administra tu catálogo de clientes. Guarda sus datos fiscales para facturar más rápido.',
    position: 'right',
    icon: Users,
    route: '/dashboard/clients'
  },
  {
    id: 'certificates',
    target: '[data-testid="nav-certificates"]',
    title: 'Certificados',
    description: 'Aquí subes tus archivos CSD para timbrar facturas y FIEL para descargar documentos del SAT automáticamente.',
    position: 'right',
    icon: FileKey,
    route: '/dashboard/certificates'
  },
  {
    id: 'whatsapp',
    target: '[data-testid="nav-chat"]',
    title: 'WhatsApp Bot',
    description: 'Chatea con nuestro bot inteligente para crear facturas, descargar tu CSF y recibir recordatorios.',
    position: 'right',
    icon: MessageSquare,
    route: '/dashboard/chat'
  },
  {
    id: 'subscription',
    target: '[data-testid="nav-subscription"]',
    title: 'Suscripción',
    description: 'Administra tu plan y método de pago. También puedes pagar directamente desde WhatsApp.',
    position: 'right',
    icon: CreditCard,
    route: '/dashboard/subscription'
  },
  {
    id: 'settings',
    target: '[data-testid="nav-settings"]',
    title: 'Configuración',
    description: 'Personaliza tu cuenta, configura impuestos, plantillas de PDF y más.',
    position: 'right',
    icon: Settings,
    route: '/dashboard/settings'
  },
  {
    id: 'complete',
    target: null,
    title: '¡Todo Listo!',
    description: 'Ya conoces las funcionalidades principales. ¿Quieres crear tu primera factura o chatear con el bot?',
    position: 'center',
    icon: Sparkles,
    actions: [
      { label: 'Crear Factura', route: '/dashboard/invoices/new', variant: 'default' },
      { label: 'Chatear con Bot', route: '/dashboard/chat', variant: 'outline' }
    ]
  }
];

const GuidedTour = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [targetRect, setTargetRect] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const step = TOUR_STEPS[currentStep];
  const Icon = step.icon;
  const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;

  // Find and highlight target element
  const updateTargetPosition = useCallback(() => {
    if (step.target) {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
        
        // Scroll element into view if needed
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        setTargetRect(null);
      }
    } else {
      setTargetRect(null);
    }
  }, [step.target]);

  useEffect(() => {
    // Navigate to the step's route if specified
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
    }
    
    // Wait for navigation and then find target
    const timeout = setTimeout(updateTargetPosition, 500);
    
    return () => clearTimeout(timeout);
  }, [currentStep, step.route, navigate, location.pathname, updateTargetPosition]);

  useEffect(() => {
    // Update position on resize
    window.addEventListener('resize', updateTargetPosition);
    return () => window.removeEventListener('resize', updateTargetPosition);
  }, [updateTargetPosition]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    setIsVisible(false);
    
    // Save tour completion to backend
    try {
      const token = localStorage.getItem('cia_token');
      await fetch(`${API_URL}/api/users/tour-completed`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Error saving tour completion:', error);
    }
    
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    setIsVisible(false);
    if (onSkip) onSkip();
  };

  const handleAction = (route) => {
    setIsVisible(false);
    navigate(route);
    if (onComplete) onComplete();
  };

  if (!isVisible) return null;

  // Calculate tooltip position
  const getTooltipStyles = () => {
    if (step.position === 'center' || !targetRect) {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }

    const padding = 16;
    const tooltipWidth = 360;
    const tooltipHeight = 200;

    switch (step.position) {
      case 'bottom':
        return {
          top: `${targetRect.bottom + padding}px`,
          left: `${targetRect.left + targetRect.width / 2}px`,
          transform: 'translateX(-50%)'
        };
      case 'top':
        return {
          top: `${targetRect.top - tooltipHeight - padding}px`,
          left: `${targetRect.left + targetRect.width / 2}px`,
          transform: 'translateX(-50%)'
        };
      case 'right':
        return {
          top: `${targetRect.top + targetRect.height / 2}px`,
          left: `${targetRect.right + padding}px`,
          transform: 'translateY(-50%)'
        };
      case 'left':
        return {
          top: `${targetRect.top + targetRect.height / 2}px`,
          left: `${targetRect.left - tooltipWidth - padding}px`,
          transform: 'translateY(-50%)'
        };
      default:
        return {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        };
    }
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 z-[9998] transition-opacity duration-300"
        onClick={handleSkip}
      />
      
      {/* Spotlight on target element */}
      {targetRect && (
        <div
          className="fixed z-[9999] ring-4 ring-blue-500 ring-offset-4 rounded-lg pointer-events-none transition-all duration-300"
          style={{
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)'
          }}
        />
      )}
      
      {/* Tooltip */}
      <div
        className="fixed z-[10000] w-[360px] bg-white rounded-xl shadow-2xl transition-all duration-300"
        style={getTooltipStyles()}
        data-testid="tour-tooltip"
      >
        {/* Progress bar */}
        <div className="h-1 bg-slate-100 rounded-t-xl overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors"
          data-testid="tour-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Content */}
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Paso {currentStep + 1} de {TOUR_STEPS.length}</p>
              <h3 className="font-bold text-slate-900">{step.title}</h3>
            </div>
          </div>
          
          <p className="text-slate-600 mb-6">{step.description}</p>
          
          {/* Actions for final step */}
          {step.actions ? (
            <div className="flex gap-3">
              {step.actions.map((action, i) => (
                <Button
                  key={i}
                  variant={action.variant}
                  onClick={() => handleAction(action.route)}
                  className="flex-1"
                >
                  {action.label}
                </Button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handlePrev}
                  disabled={currentStep === 0}
                  className="text-slate-500"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Anterior
                </Button>
                
                <Button variant="ghost" size="sm" onClick={handleSkip} className="text-slate-500">
                  Saltar Tour
                </Button>
                
                <Button size="sm" onClick={handleNext}>
                  {currentStep === TOUR_STEPS.length - 1 ? 'Finalizar' : 'Siguiente'}
                  {currentStep < TOUR_STEPS.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Hook to manage tour state
export const useTour = () => {
  const [showTour, setShowTour] = useState(false);
  const [tourCompleted, setTourCompleted] = useState(false);

  useEffect(() => {
    // Check if user has completed tour
    const completed = localStorage.getItem('cia_tour_completed');
    setTourCompleted(completed === 'true');
  }, []);

  const startTour = () => {
    setShowTour(true);
  };

  const completeTour = () => {
    setShowTour(false);
    setTourCompleted(true);
    localStorage.setItem('cia_tour_completed', 'true');
  };

  const skipTour = () => {
    setShowTour(false);
  };

  const resetTour = () => {
    setTourCompleted(false);
    localStorage.removeItem('cia_tour_completed');
  };

  return {
    showTour,
    tourCompleted,
    startTour,
    completeTour,
    skipTour,
    resetTour
  };
};

export default GuidedTour;
